import {
  AlertTriangle, ArrowLeft, Camera, Check, CheckCircle2, ChevronRight, Image as ImageIcon,
  ClipboardCheck, Gauge, HeartPulse, LogOut, PencilLine, RotateCcw, ShieldCheck, Truck, UserRound,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { APP_DATE, APP_DATE_JA } from '../data/seed'
import {
  DRIVER_CONDITION_ITEMS, VEHICLE_INSPECTION_ITEMS, completedSafetyValues, emptySafetyValues,
} from '../data/safetyChecks'
import { Button, Field, LoadingBlock } from '../components/shared'

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export default function DriverFlow({ driver, vehicles, settings, records = [], appDate = APP_DATE, appDateJa = APP_DATE_JA, onSave, onLogout }) {
  const assignedVehicles = useMemo(() => vehicles.filter((vehicle) => driver.vehicleIds.includes(vehicle.id) && vehicle.active), [driver, vehicles])
  const [step, setStep] = useState(1)
  const [vehicleId, setVehicleId] = useState(assignedVehicles[0]?.id || '')
  const [phase, setPhase] = useState('業務前')
  const [driverCondition, setDriverCondition] = useState(() => emptySafetyValues(DRIVER_CONDITION_ITEMS))
  const [vehicleInspection, setVehicleInspection] = useState(() => emptySafetyValues(VEHICLE_INSPECTION_ITEMS))
  const [safetyNote, setSafetyNote] = useState('')
  const [method, setMethod] = useState('image')
  const [value, setValue] = useState('')
  const imageRequest = useRef(0)
  const [imageData, setImageData] = useState('')
  const [imageLoading, setImageLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errors, setErrors] = useState({})
  const cameraRef = useRef(null)
  const uploadRef = useRef(null)
  const vehicle = vehicles.find((item) => item.id === vehicleId)
  const numericValue = Number(value)
  const alcoholRequiresReview = value !== '' && Number.isFinite(numericValue) && numericValue > 0
  const safetyValues = [...Object.values(driverCondition), ...Object.values(vehicleInspection)]
  const safetyIncomplete = phase === '業務前' && safetyValues.some((item) => item === null)
  const safetyRequiresReview = phase === '業務前' && safetyValues.some((item) => item === 'review')
  const requiresReview = alcoholRequiresReview || safetyRequiresReview
  const previousChecks = records.filter(record => record.driverId === driver.id && record.date === appDate && record.phase === phase)
  const steps = phase === '業務前' ? ['車両', '安全点呼', '測定', '確認'] : ['車両', '測定', '確認']
  const measureStep = phase === '業務前' ? 3 : 2
  const confirmStep = phase === '業務前' ? 4 : 3
  const beforeCheckCompleted = records.some((record) => record.driverId === driver.id && record.date === appDate && record.phase === '業務前')

  const chooseVehicle = (id) => {
    setVehicleId(id)
    setErrors({})
  }

  const goNextFromVehicle = () => {
    if (!vehicleId) return setErrors({ vehicle: '本日乗車する車両を選択してください' })
    setStep(2)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const setSafetyValue = (group, id, nextValue) => {
    const setter = group === 'driver' ? setDriverCondition : setVehicleInspection
    setter((current) => ({ ...current, [id]: nextValue }))
    setErrors((current) => ({ ...current, safety: '', safetyNote: '' }))
  }

  const markAllSafetyOk = () => {
    setDriverCondition(completedSafetyValues(DRIVER_CONDITION_ITEMS))
    setVehicleInspection(completedSafetyValues(VEHICLE_INSPECTION_ITEMS))
    setErrors((current) => ({ ...current, safety: '', safetyNote: '' }))
  }

  const goMeasureFromSafety = () => {
    const nextErrors = {}
    if (safetyIncomplete) nextErrors.safety = 'すべての体調・車両項目を確認してください'
    if (safetyRequiresReview && safetyNote.trim().length < 3) nextErrors.safetyNote = '異常の内容と管理者へ伝える事項を入力してください'
    if (Object.keys(nextErrors).length) return setErrors(nextErrors)
    setStep(measureStep)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleImage = async (file) => {
    if (!file) return
    const request = ++imageRequest.current
    setValue(''); setConfirmed(false); setImageData(''); setErrors({})
    if (!file.type.startsWith('image/')) {
      setErrors({ image: '画像ファイルを選択してください' })
      return
    }
    setImageLoading(true)
    try {
      const dataUrl = await readAsDataUrl(file)
      if (request === imageRequest.current) setImageData(dataUrl)
    } catch (error) {
      if (request === imageRequest.current) setErrors({ image: '写真を開けませんでした。撮り直してください。' })
    } finally {
      if (request === imageRequest.current) setImageLoading(false)
    }
  }

  const goConfirm = () => {
    const nextErrors = {}
    if (!/^\d(?:\.\d{1,2})?$/.test(value) || !Number.isFinite(numericValue) || numericValue < 0 || numericValue > 2) nextErrors.value = '0.00〜2.00の範囲で数値を入力してください'
    if (!imageData || imageLoading) nextErrors.image = '今回の測定結果の写真を保存してください'
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors)
      return
    }
    setValue(numericValue.toFixed(2))
    setConfirmed(false)
    setStep(confirmStep)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const submit = async () => {
    if (!confirmed) {
      setErrors({ confirm: '表示内容と目視確認の結果を確認してください' })
      return
    }
    const now = new Date()
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    setSaving(true)
    setErrors((current) => ({ ...current, submit: '' }))
    try {
      await onSave({
        id: `rec-${Date.now()}`, date: appDate, time, phase, driverId: driver.id, vehicleId,
        value: numericValue, method: '手入力', imageDataUrl: imageData,
        presence: alcoholRequiresReview, visualCheck: '未確認', confirmationMethod: '未確認', checkerId: settings.defaultCheckerId || 'ALC-TOKYO-01',
        confirmer: '管理者確認待ち',
        instruction: alcoholRequiresReview ? '運転中止・管理者へ連絡' : '運転開始前に管理者の確認を受けてください',
        note: `${method === 'image' ? '写真の0.00表示をドライバーが確認（OCR不使用）' : '写真の実測値を手入力'}。管理者確認待ち。${safetyNote.trim()}${previousChecks.length ? ` 再測定：同日の先行記録 ${previousChecks.map(record => record.id).join(', ')} を保持。` : ''}`,
        preDriveCheck: phase === '業務前' ? {
          driverCondition,
          vehicleInspection,
          status: safetyRequiresReview ? 'review' : 'completed',
          note: safetyNote.trim(),
          checkedAt: now.toISOString(),
        } : null,
        status: 'review', createdAt: now.toISOString(),
        audit: [`${time} ${driver.name}が写真と表示値を記録`, `${time} 管理者確認待ち（自己確認と区別）`],
      })
      setSuccess(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      setErrors((current) => ({ ...current, submit: error.message || '記録を保存できませんでした' }))
    } finally {
      setSaving(false)
    }
  }

  const restart = () => {
    setStep(1); setMethod('image'); setValue(''); setImageData(''); setSuccess(false); setConfirmed(false); setErrors({}); setSafetyNote('')
    setDriverCondition(emptySafetyValues(DRIVER_CONDITION_ITEMS)); setVehicleInspection(emptySafetyValues(VEHICLE_INSPECTION_ITEMS))
  }

  if (success) {
    return (
      <main className="driver-app driver-success">
        <div className="driver-topbar"><div className="brand-mark"><ShieldCheck /><span>ALCO LOG</span></div><button onClick={onLogout}><LogOut />ログアウト</button></div>
        <section className={`success-panel ${requiresReview ? 'success-panel--review' : ''}`}>
          {requiresReview ? <AlertTriangle /> : <CheckCircle2 />}
          <h1>管理者確認待ちで登録しました</h1>
          <p>{appDateJa} {phase}</p>
          <strong className="success-value">{value}<small>mg/L</small></strong>
          <dl>
            <div><dt>ドライバー</dt><dd>{driver.name}</dd></div>
            <div><dt>車両</dt><dd>{vehicle?.number}</dd></div>
            <div><dt>管理者確認</dt><dd>未確認</dd></div>
          </dl>
          {requiresReview ? <div className="danger-instruction"><AlertTriangle />{alcoholRequiresReview ? '運転を開始せず、安全運転管理者の指示を受けてください。' : '運転開始前に、安全運転管理者へ報告して確認を受けてください。'}</div> : null}
          <p>写真の保存だけでは管理者確認は完了しません。実際の確認を受けてください。再測定しても今回の記録は残ります。</p>
          <Button icon={RotateCcw} variant="secondary" onClick={restart}>別のチェックを記録</Button>
          <Button variant="ghost" onClick={onLogout}>ログアウト</Button>
        </section>
      </main>
    )
  }

  return (
    <main className="driver-app">
      <header className="driver-header">
        <div className="driver-header__row">
          {step > 1 ? <button className="icon-button" aria-label="前の手順へ" onClick={() => setStep(step - 1)}><ArrowLeft /></button> : <span className="driver-header__spacer" />}
          <h1>アルコールチェック</h1>
          <button className="driver-user" onClick={onLogout} title="ログアウト"><UserRound /><span>{driver.name}</span></button>
        </div>
        <div className="driver-date">{appDateJa}</div>
      </header>

      <ol className="step-rail" aria-label="入力手順">
        {steps.map((label, index) => {
          const number = index + 1
          const done = number < step
          return <li key={label} className={number === step ? 'active' : done ? 'done' : ''}><span>{done ? <Check /> : number}</span><b>{label}</b></li>
        })}
      </ol>

      {step === 1 ? (
        <section className="driver-section vehicle-step">
          <h2>本日乗る車両を選択</h2>
          <p>登録後はこの車両番号と測定記録が紐づきます。</p>
          {!beforeCheckCompleted ? <div className="driver-reminder"><AlertTriangle /><span><strong>業務前チェックが未実施です</strong>業務操作は継続できますが、忘れずにチェックしてください。</span></div> : null}
          <div className="phase-switch" aria-label="確認区分">
            {['業務前', '業務後'].map((item) => <button key={item} onClick={() => { setPhase(item); setErrors({}) }} className={phase === item ? 'active' : ''}>{item}</button>)}
          </div>
          <div className="vehicle-options">
            {assignedVehicles.map((item) => (
              <button key={item.id} className={vehicleId === item.id ? 'selected' : ''} onClick={() => chooseVehicle(item.id)}>
                <span className="vehicle-options__icon"><Truck /></span>
                <span><strong>{item.number}</strong><small>{item.type}／{item.model}</small><em>{item.branch}</em></span>
                <span className="radio-mark">{vehicleId === item.id ? <Check /> : null}</span>
              </button>
            ))}
          </div>
          {errors.vehicle ? <p className="inline-error">{errors.vehicle}</p> : null}
          <div className="mobile-action"><Button onClick={goNextFromVehicle}>{phase === '業務前' ? '安全点呼へ' : 'この車両で測定へ'}<ChevronRight /></Button></div>
        </section>
      ) : null}

      {phase === '業務前' && step === 2 ? (
        <section className="driver-section safety-step">
          <button className="selected-vehicle-line" onClick={() => setStep(1)}><Truck /><span><strong>{vehicle?.number}</strong><small>{vehicle?.type}／{vehicle?.model}・業務前</small></span><ChevronRight /></button>
          <h2>運転前の安全点呼</h2>
          <p>体調と日常点検をまとめて記録します。異常があっても記録は続けられますが、運転前に管理者へ報告してください。</p>
          <Button variant="secondary" icon={ClipboardCheck} className="safety-all-ok" onClick={markAllSafetyOk}>確認して全項目を「問題なし」にする</Button>
          <SafetyCheckGroup icon={HeartPulse} title="運転者の状態" items={DRIVER_CONDITION_ITEMS} values={driverCondition} onChange={(id, nextValue) => setSafetyValue('driver', id, nextValue)} />
          <SafetyCheckGroup icon={Gauge} title="車両の日常点検" items={VEHICLE_INSPECTION_ITEMS} values={vehicleInspection} onChange={(id, nextValue) => setSafetyValue('vehicle', id, nextValue)} />
          {errors.safety ? <p className="inline-error">{errors.safety}</p> : null}
          <Field label="異常・連絡事項" required={safetyRequiresReview} error={errors.safetyNote} hint="異常がある場合は、症状・管理者への連絡内容・代替車両などを記録">
            <textarea rows="3" value={safetyNote} onChange={(event) => setSafetyNote(event.target.value)} placeholder="例：警告灯が点灯。管理者へ連絡し、予備車両を確認" />
          </Field>
          {safetyRequiresReview ? <div className="danger-instruction"><AlertTriangle />要確認項目があります。記録後、運転開始前に管理者へ報告してください。</div> : null}
          <div className="mobile-action"><Button onClick={goMeasureFromSafety}>アルコール測定へ<ChevronRight /></Button></div>
        </section>
      ) : null}

      {step === measureStep ? (
        <section className="driver-section measurement-step">
          <button className="selected-vehicle-line" onClick={() => setStep(1)}><Truck /><span><strong>{vehicle?.number}</strong><small>{vehicle?.type}／{vehicle?.model}・{phase}</small></span><ChevronRight /></button>
          <h2>測定値を記録</h2>
          <div className="checker-status"><ShieldCheck /><span><small>使用する検知器</small><strong>{settings.defaultCheckerId || 'ALC-TOKYO-01'}</strong><em>機種・使用期限・作動点検日は本番設定で管理</em></span></div>
          <p>今回の測定結果を撮影し、写真と実際の表示値を確認してください。OCRによる自動判定は行いません。</p>
            <div className="image-method">
              <div className={`camera-preview ${imageData ? 'camera-preview--uploaded' : ''} ${errors.image ? 'camera-preview--error' : ''}`}>
                <img src={imageData || '/assets/alcohol-checker-sample.png'} alt={imageData ? '選択したアルコールチェッカー画像' : 'アルコールチェッカー撮影例'} />
                {!imageData ? <span className="camera-frame" aria-hidden="true" /> : null}
                {!imageData ? <span className="sample-label">撮影例</span> : null}
                {imageLoading ? <div className="ocr-overlay"><LoadingBlock label="写真を準備中" /></div> : null}
              </div>
              <div className="camera-actions">
                <Button icon={Camera} disabled={imageLoading} onClick={() => cameraRef.current?.click()}>カメラで撮影</Button>
                <Button icon={ImageIcon} disabled={imageLoading} variant="secondary" onClick={() => uploadRef.current?.click()}>写真を選択</Button>
              </div>
              <input ref={cameraRef} className="visually-hidden" type="file" accept="image/*" capture="environment" onChange={(event) => handleImage(event.target.files?.[0])} />
              <input ref={uploadRef} className="visually-hidden" type="file" accept="image/*" onChange={(event) => handleImage(event.target.files?.[0])} />
              {errors.image ? <p className="inline-error">{errors.image}</p> : null}
            </div>
          <div className="camera-actions">
            <Button disabled={!imageData || imageLoading} onClick={() => { setMethod('image'); setValue('0.00'); setErrors({}); setConfirmed(false) }}>表示が0.00 mg/Lであることを確認</Button>
            <Button variant="secondary" onClick={() => { setMethod('manual'); setValue(''); setConfirmed(false) }}>0.00以外・判定できない</Button>
          </div>
          {method === 'manual' ? (
            <div className="manual-method">
              <p>写真に写っている実測値を入力してください。表示を判定できない場合は撮り直し、機器のエラー等は管理者へ連絡してください。未確認を0.00として登録しないでください。</p>
              <Field label="チェッカーの表示値" required error={errors.value} hint="小数点以下2桁で入力してください">
                <div className="measurement-input"><input inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value.replace(/[^0-9.]/g, ''))} aria-label="測定値" /><span>mg/L</span></div>
              </Field>
            </div>
          ) : null}
          <div className={`detected-result ${alcoholRequiresReview ? 'detected-result--review' : ''}`}>
            <span>確認した表示値</span><strong>{value || '未確認'} <small>mg/L</small></strong>
          </div>
          {errors.value ? <p className="inline-error">{errors.value}</p> : null}
          <p className={alcoholRequiresReview ? 'result-warning result-warning--danger' : 'result-warning'}><AlertTriangle />{alcoholRequiresReview ? '基準値を超えています。運転せず管理者へ連絡してください。' : '数値を確認してから次へ進んでください'}</p>
          {previousChecks.length ? <p>本日の同区分の記録：{previousChecks.length}件。今回の記録は追加保存され、前の測定結果は消えません。</p> : null}
          <div className="mobile-action"><Button disabled={imageLoading || !imageData || !value} onClick={goConfirm}>確認画面へ<ChevronRight /></Button><p>写真と表示値が一致していることを確認してください</p></div>
        </section>
      ) : null}

      {step === confirmStep ? (
        <section className="driver-section confirmation-step">
          <h2>登録内容を確認</h2>
          <p>記録後の測定値は、管理者の監査履歴に残ります。</p>
          <div className={`confirm-value ${requiresReview ? 'confirm-value--review' : ''}`}><span>管理者確認待ち</span><strong>{value}<small>mg/L</small></strong></div>
          <dl className="confirm-list">
            <div><dt>ドライバー</dt><dd>{driver.name}</dd></div>
            <div><dt>車両</dt><dd>{vehicle?.number}<small>{vehicle?.type}／{vehicle?.model}</small></dd></div>
            <div><dt>日時・区分</dt><dd>{appDateJa}<small>{phase}</small></dd></div>
            <div><dt>記録方法</dt><dd>{method === 'image' ? '写真＋0.00の目視確認' : '写真＋実測値の手入力'}（OCR不使用）</dd></div>
            {phase === '業務前' ? <div><dt>安全点呼</dt><dd className={safetyRequiresReview ? 'text-review' : ''}>{safetyRequiresReview ? '要確認' : '体調・日常点検とも良好'}{safetyNote ? <small>{safetyNote}</small> : null}</dd></div> : null}
          </dl>
          <p>ドライバー本人による記録です。管理者による対面・通話等の確認は、別途管理者画面から登録します。0.00の写真だけで運転可とは判定しません。</p>
          <label className={`confirm-check ${errors.confirm ? 'confirm-check--error' : ''}`}><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>表示内容と確認結果に誤りがないことを確認しました</span></label>
          {errors.confirm ? <p className="inline-error">{errors.confirm}</p> : null}
          {requiresReview ? <div className="danger-instruction"><AlertTriangle />この記録は要確認として保存されます。{alcoholRequiresReview ? '運転を開始できません。' : '運転前に管理者へ報告してください。'}</div> : null}
          {errors.submit ? <p className="inline-error">{errors.submit}</p> : null}
          <div className="mobile-action"><Button onClick={submit} disabled={saving}>{saving ? <LoadingBlock label="保存中" /> : requiresReview ? '要確認として登録' : 'この内容で登録'}</Button><Button variant="ghost" onClick={() => setStep(measureStep)} disabled={saving}>測定値を修正</Button></div>
        </section>
      ) : null}
    </main>
  )
}

function SafetyCheckGroup({ icon: Icon, title, items, values, onChange }) {
  return (
    <section className="safety-check-group">
      <header><Icon /><h3>{title}</h3></header>
      <div>
        {items.map((item) => (
          <div className="safety-check-row" key={item.id}>
            <span>{item.label}</span>
            <div role="group" aria-label={`${item.label}の結果`}>
              <button type="button" className={values[item.id] === 'ok' ? 'ok active' : 'ok'} onClick={() => onChange(item.id, 'ok')}><Check />問題なし</button>
              <button type="button" className={values[item.id] === 'review' ? 'review active' : 'review'} onClick={() => onChange(item.id, 'review')}><AlertTriangle />要確認</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
